<script setup lang="ts">
import ButtonClear from './ButtonClear.vue';
import { IconError } from '../../../icons';

import { EFormItemSize } from '../../models/size';
import { computed, ref } from 'vue';
import type { TInputBaseProps } from '../../models/TInputBaseProps';

const model = defineModel<string>({ required: true });

const props = withDefaults(defineProps<TInputBaseProps>(), {
  size: EFormItemSize.m40,
  isInvalid: false,
  disabled: false,
  placeholder: '',
  label: '',
});

const emit = defineEmits<{
  (e: 'focus' | 'blur'): void;
}>();

const isFocused = ref(false);
const isHovered = ref(false);
const inputEl = ref<HTMLInputElement>();

function onInput(e: Event) {
  const value = (e.target as HTMLInputElement).value;

  model.value = value;
}

const isClearButtonShown = computed(() => {
  if (!model.value) return false;
  return isHovered.value || isFocused.value;
});

function onFocus() {
  isFocused.value = true;
  emit('focus');
}

function onBlur() {
  isFocused.value = false;
  emit('blur');
}

const focus = () => {
  inputEl.value?.focus();
};

defineExpose({ focus });
</script>

<template>
  <div
    class="input-field-container"
    :class="{ 'input-field-container_disabled': disabled }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <input
      ref="inputEl"
      type="text"
      :value="model"
      :placeholder="placeholder"
      class="input-field-container__input"
      :class="`input-field-container__input_${size}`"
      :disabled="disabled"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
    />

    <ButtonClear v-if="isClearButtonShown" @click="model = ''" />
    <IconError v-if="isInvalid" class="input-field-container__icon-error" />
  </div>
</template>

<style lang="stylus" scoped>
.input-field-container
  display flex
  align-items center
  overflow hidden
  min-height 28px
  gap 8px
  width 100%

  &_disabled
    pointer-events none

  &__input
    padding 0
    width 100%
    font-family var(--typography-font)
    font-weight 400
    outline none
    text-overflow ellipsis
    color var(--text-primary)
    background-color transparent

    &:disabled
      color var(--text-disabled)
      background none

    &_m40
      font-size 14px
      line-height 20px
    &_l48
      font-size 16px
      line-height 22px

    &::placeholder
      color var(--text-tertiary)

  &__icon-error
    color var(--icon-negative)
    font-size 20px
</style>
